import { AdvizeoModalService } from '@/services/advizeo-modal.service';
import { wait } from '@/utils/toolkit/promise/wait.utils';
import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, type Observable, Subject } from 'rxjs';
import { first, map } from 'rxjs/operators';
import type { CompletenessMode } from '../../interfaces/DeliveryPointIndicator.interface';
import type { CoreProjectCreationRequestBody, Project, ProjectId, ProjectRequestParams } from '../../interfaces/Project.interface';
import type { DeliveryPointMetric } from '../../interfaces/Timeseries.interface';
import type { AppState } from '../../store/app.state';
import { ProjectsStoreActions, ProjectsStoreSelectors } from '../../store/entities/projects';
import type { VatModes } from '../budgets/budgets.constants';
import { StoreProvider } from '../store.provider';
import { AdvizeoCoreProjectsProvider } from './advizeo-core-projects.provider';

@Injectable({
    providedIn: 'root'
})
export class ProjectsProvider {
    private _advizeoCoreProjectsProvider = inject(AdvizeoCoreProjectsProvider);
    private _store = inject<Store<AppState>>(Store);
    private _storeProvider = inject(StoreProvider);
    private _advizeoModalService = inject(AdvizeoModalService);
    private _translate = inject(TranslateService);

    private _projectCreated$ = new Subject<ProjectId>();

    fetchUserProjects(selectedProjectId: string): Promise<Project[]> {
        return new Promise((resolve, reject) => {
            this._store.dispatch(new ProjectsStoreActions.FetchAll());

            firstValueFrom(this._advizeoCoreProjectsProvider.fetchUserProjects$())
                .then(async projects => {
                    // TODO: Delete these lines when allowUpdateOnAssetData will be integrated in the backend (ADV-3228)
                    // Start of lines to delete
                    projects = projects.map(proj => {
                        return {
                            ...proj,
                            allowUpdateOnAssetData: true
                        };
                    });
                    // End of lines to delete

                    this._store.dispatch(new ProjectsStoreActions.FetchAllSucceeded({ projects, selectedProjectId }));
                    resolve(projects);
                })
                .catch(err => {
                    this._store.dispatch(new ProjectsStoreActions.FetchAllFailed());
                    reject(err);
                });
        });
    }

    isChangingProject$(): Observable<boolean> {
        return this._store.pipe(select(ProjectsStoreSelectors.selectIsChanging));
    }

    requestProjectCreation(): Promise<void> {
        const modal = this._advizeoModalService.openOneFieldModal({
            title: this._translate.instant('POPOVERS.PROJECT_CREATION.TITLE'),
            fieldLabel: this._translate.instant('POPOVERS.PROJECT_CREATION.QUESTION'),
            fieldType: 'text',
            submitValidators: [
                {
                    validate: name => !!name && name.length > 1,
                    errorMessage: this._translate.instant('ERRORS.FORM.MIN_LENGTH', { min: 1 })
                }
            ]
        });

        return modal.afterClosed().then(data => {
            if (data && data.fieldValue) {
                this.createProject({
                    name: data.fieldValue
                });
            }
        });
    }

    createProject(body: CoreProjectCreationRequestBody): Promise<string> {
        return firstValueFrom(this._advizeoCoreProjectsProvider.createProject$(body))
            .then(projectId => {
                this._store.dispatch(
                    new ProjectsStoreActions.CreateProject({
                        project: {
                            projectId,
                            roles: [],
                            name: body.name,
                            logoUrl: null,
                            bigPictureUrl: null,
                            isVatIncl: false,
                            allowUpdateOnAssetData: true,
                            completenessSources: [],
                            completenessMode: null,
                            completenessThreshold: null,
                            complianceTunnel: null,
                            dataQualitySource1: null,
                            dataQualitySource2: null,
                            defaultProfileId: null,
                            allowedDashboardCount: null,
                            fileSizeQuota: null
                        } as any
                    })
                );

                wait(1000).then(() => {
                    this._projectCreated$.next(projectId);
                });

                return projectId;
            })
            .catch(err => {
                console.log('error while creating project: ', err);
                throw err;
            });
    }

    listenToProjectCreation$(): Observable<ProjectId> {
        return this._projectCreated$;
    }

    getUserProjects$(): Observable<Project[]> {
        return this._store.pipe(select(ProjectsStoreSelectors.selectProjects));
    }

    getProjectById$(projectId: string): Observable<Project | null> {
        return this.getUserProjects$().pipe(map(projects => projects.find(element => element.projectId === projectId) ?? null));
    }

    getUserCurrentProjectId$(): Observable<ProjectId | null> {
        return this._store.pipe(select(ProjectsStoreSelectors.selectCurrentProjectId));
    }

    getUserCurrentProject$(): Observable<Project | null> {
        return this._store.pipe(select(ProjectsStoreSelectors.selectCurrentProject));
    }

    getUserCurrentProject(): Project | null {
        return this._storeProvider.getStoreInstantValue(ProjectsStoreSelectors.selectCurrentProject);
    }

    getUserCurrentProjectId(): ProjectId | null {
        const currentProject = this.getUserCurrentProject();
        return currentProject ? currentProject.projectId : null;
    }

    async setUserCurrentProjectId(projectId: string): Promise<void> {
        if (!projectId) return;
        this._store.dispatch(new ProjectsStoreActions.SetCurrentProjectId({ projectId }));
    }

    getVatMode$(): Observable<VatModes | null> {
        return this._store.pipe(select(ProjectsStoreSelectors.selectCurrentProjectVatMode));
    }

    getVatMode(): VatModes | null {
        return this._storeProvider.getStoreInstantValue(ProjectsStoreSelectors.selectCurrentProjectVatMode);
    }

    updateProject(projectId: string, project: Partial<ProjectRequestParams>): Promise<void> {
        return firstValueFrom(this._advizeoCoreProjectsProvider.updateProject$(projectId, project)).then(() => {
            this._store.dispatch(
                new ProjectsStoreActions.UpdateProject({
                    projectId,
                    project
                })
            );
        });
    }

    waitForProjectsStateLoad(): Promise<boolean> {
        return firstValueFrom(
            this._store.pipe(
                select(ProjectsStoreSelectors.selectIsLoaded),
                first(isLoaded => isLoaded)
            )
        );
    }

    getCurrentProjectCompletenessSources$(): Observable<DeliveryPointMetric[]> {
        return this._store.pipe(select(ProjectsStoreSelectors.selectCurrentProjectCompletenessSources));
    }

    getCurrentProjectCompletenessMode(): CompletenessMode | null {
        return this._storeProvider.getStoreInstantValue(ProjectsStoreSelectors.selectCurrentProjectCompletenessMode);
    }

    areProjectsLoaded$(): Observable<boolean> {
        return this._store.pipe(select(ProjectsStoreSelectors.selectIsLoaded));
    }
}
